import { ICON__chevron_down } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { UTILS__ml_asset } from '@/utils/UTILS__ml_asset';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD_PRIMITIVE__base_string } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/FIELD_PRIMITIVE__base_string';
import { useState } from 'react';
import { HOOK__ml_asset_name_and_version } from '../../_hooks/HOOK__ml_asset_name_and_version';

interface PROPS {
  field: FIELD_PRIMITIVE__base_string;
}

export function G_UI_FIELD_PRIMITIVE__string_ml_asset_hash_dropdown({ field }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  let { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */
  const [value, set_value] = useState(field.get_value());

  const { name: current_name, version: current_version } = HOOK__ml_asset_name_and_version({ hash: value });
  const trigger_id = `G_UI_FIELD_PRIMITIVE__string_ml_asset_hash_dropdown_${field.meta.field_id}`;

  /*
   * EFFECTS
   */
  use_effect(() => set_value(field.get_value()), [field.value]);

  /*
   * EVENTS
   */
  async function on_item_selected(value_new: string) {
    if (value_new == field.get_value()) return;

    let field_new = field.clone({ value: value_new });

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);
  }

  function is_disabled() {
    return g_general__ui_disabled;
  }

  function get_cursor() {
    return is_disabled() ? 'cursor-not-allowed' : 'cursor-pointer';
  }

  /*
   * RENDERS
   */

  function render_item(hash: string) {
    const ml_asset = UTILS__ml_asset.get_by_hash(hash);

    if (ml_asset.is_stub()) return null;

    const { name, version } = ml_asset;

    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={`${trigger_id}_item_${hash}`}
        onClick={() => on_item_selected(hash)}
        value={hash}
        className={`
          w-full 
          text-left text-bar-foreground truncate
          cursor-pointer rounded-lg           
          hover:bg-accent   
          ${value == hash ? 'bg-accent' : ''}
        `}
      >
        <div className="flex gap-2 truncate">
          <div className="w-28 max-w-28 truncate">{name}</div>
          <div className="w-28 max-w-28 truncate">{version}</div>
        </div>
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_bar_line />;

  return (
    <div className="size-full p-1">
      <UI_PRIMITIVE__dropdown_menu>
        <UI_PRIMITIVE__dropdown_menu_trigger asChild>
          <UI_PRIMITIVE__button
            data-component={trigger_id}
            id={trigger_id}
            variant="outline-on-bar"
            disabled={is_disabled()}
            className={`relative flex flex-row justify-start items-center
            px-2 
            w-full h-7
            ${get_cursor()}`}
          >
            <span className="max-w-44 truncate text-sm">
              {current_name}
              {current_version}
            </span>
            <ICON__chevron_down className="size-3 absolute right-4 top-1/2 -translate-y-1/2" />
          </UI_PRIMITIVE__button>
        </UI_PRIMITIVE__dropdown_menu_trigger>
        <UI_PRIMITIVE__dropdown_menu_content {...{ trigger_id }} className="rounded-lg">
          {field.get_value_range().map(render_item)}
        </UI_PRIMITIVE__dropdown_menu_content>
      </UI_PRIMITIVE__dropdown_menu>
    </div>
  );
}
